
import { supabase } from "@/integrations/supabase/client";
import { getUserEmail } from "./getUserEmail";
import { shouldNotifyUser } from "./notificationPreferences";
import { sendEmail } from "./emailUtils";

// Send a notification about an update to a swap request
export const sendSwapUpdateNotification = async (
  swapRequestId: string,
  reagentName: string,
  recipientId: string,
  action: "updated" | "canceled",
  message?: string
): Promise<boolean> => {
  try {
    // Check if the user wants to receive swap response notifications
    const shouldNotify = await shouldNotifyUser(recipientId, "swap_responses");
    if (!shouldNotify) {
      console.log("User has disabled swap response notifications");
      return false;
    }
    
    // Get the recipient's email
    const recipientEmail = await getUserEmail(recipientId);
    if (!recipientEmail) {
      console.error("Could not find recipient's email");
      return false;
    }
    
    // Get the requester's username for the email
    const { data: requesterProfile } = await supabase
      .from("profiles")
      .select("username")
      .eq("id", recipientId)
      .single();
      
    const requesterName = requesterProfile?.username || "A user";
    
    // Compose the email
    const subject = `LabSwApp - A swap request for ${reagentName} has been ${action}`;
    const updatedText = action === "updated" 
      ? `The swap request has been updated. ${message || ''}`
      : `The swap request has been canceled.`;
    
    const html = `
      <h2>Swap Request ${action.charAt(0).toUpperCase() + action.slice(1)}</h2>
      <p>A swap request for your reagent <strong>${reagentName}</strong> has been ${action} by the requester.</p>
      <p>${updatedText}</p>
      <p>You can view all your swap requests in the My Reagents section of your account.</p>
    `;
    
    console.log(`Sending swap ${action} notification to ${recipientEmail} for reagent ${reagentName}`);
    
    // Send the email
    const emailSuccess = await sendEmail(recipientEmail, subject, html);
    
    // Also send push notification
    try {
      const { sendPushNotification } = await import("./oneSignalService");
      await sendPushNotification(
        [recipientId],
        `Swap Request ${action.charAt(0).toUpperCase() + action.slice(1)}`,
        `A swap request for ${reagentName} has been ${action}.`,
        {
          type: 'swap_update',
          swapRequestId: swapRequestId,
          reagentName: reagentName,
          action: action,
          url: `/swap-request/${swapRequestId}`
        }
      );
    } catch (pushError) {
      console.error('Failed to send push notification:', pushError);
    }
    
    return emailSuccess;
  } catch (error) {
    console.error(`Exception in sendSwap${action.charAt(0).toUpperCase() + action.slice(1)}Notification:`, error);
    return false;
  }
};
